// Menu "Saída de linha": lista de SKUs em acompanhamento para possível retirada
// do mix, com busca por marca/nome e cadastro de um novo item. O detalhe (timeline
// + lançamentos + decisões) fica em SaidaLinhaProduto.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { isAdminOrGestor } from '../lib/rbac';
import { loadBrands, createBrand, type BrandRow } from '../lib/data';
import { loadPeople, type Person } from '../lib/notifications';
import { uploadDiscImage } from '../lib/photos';
import { lookupProductByCodigo, syncProductPhoto } from '../lib/productLink';
import { ultimoMesLiberado } from '../shared/fechamento';
import {
  loadDiscontinuations,
  createDiscontinuation,
  updateDiscontinuation,
  loadEntries,
  planMonthsList,
  monthLabel,
  DISC_STATUS_LABELS,
  type Discontinuation,
  type DiscStatus,
} from '../lib/discontinuations';
import { buildListReportHtml, printReport, prepararFotos, type ReportSpec } from '../lib/report';

const brl = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
const pct = (n: number | null | undefined) =>
  n == null ? '—' : `${n.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;

export function SaidaLinha() {
  const { currentOrgId, grants, userId, fullName } = useAuth();
  const nav = useNavigate();
  const canManage = grants ? isAdminOrGestor(grants) : false;
  const [items, setItems] = useState<Discontinuation[]>([]);
  const [loading, setLoading] = useState(true);
  const [pend, setPend] = useState<Set<string>>(new Set());
  const [q, setQ] = useState('');
  const [brandFilter, setBrandFilter] = useState('todas');
  const [statusFilter, setStatusFilter] = useState<'ativos' | 'todos' | DiscStatus>('ativos');
  const [adding, setAdding] = useState(false);

  async function reload() {
    if (!currentOrgId) return;
    setLoading(true);
    const list = await loadDiscontinuations(currentOrgId);
    setItems(list);
    setLoading(false);
    // marca quem está sem o lançamento do último mês JÁ FECHADO (lembrete); o mês em andamento
    // só é liberado após o fechamento (por volta do dia 10 do mês seguinte)
    const thisMonth = ultimoMesLiberado();
    const missing = new Set<string>();
    await Promise.all(
      list
        .filter((d) => d.status === 'em_acompanhamento')
        .map(async (d) => {
          const months = planMonthsList(d.startDate, d.planMonths);
          if (!months.includes(thisMonth)) return;
          const entries = await loadEntries(d.id);
          if (!entries.some((e) => e.refMonth === thisMonth)) missing.add(d.id);
        }),
    );
    setPend(missing);
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrgId]);

  const brandOptions = useMemo(() => {
    const s = new Map<string, string>();
    items.forEach((i) => i.brandName && s.set(i.brandName, i.brandName));
    return [...s.keys()].sort();
  }, [items]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return items.filter((i) => {
      if (brandFilter !== 'todas' && i.brandName !== brandFilter) return false;
      if (statusFilter === 'ativos' && i.status !== 'em_acompanhamento') return false;
      if (statusFilter !== 'ativos' && statusFilter !== 'todos' && i.status !== statusFilter) return false;
      if (term && !`${i.name} ${i.brandName ?? ''} ${i.codigoInterno ?? ''}`.toLowerCase().includes(term))
        return false;
      return true;
    });
  }, [items, q, brandFilter, statusFilter]);

  const [gerando, setGerando] = useState(false);
  async function relatorioGeral() {
    if (!filtered.length) return;
    setGerando(true);
    try {
      const specs: ReportSpec[] = [];
      for (const d of filtered) {
        const months = planMonthsList(d.startDate, d.planMonths);
        const entries = await loadEntries(d.id);
        const byM = new Map(entries.map((e) => [e.refMonth, e]));
        const pts = (pick: (e: typeof entries[number]) => number | null) =>
          months.map((m) => ({ label: monthLabel(m), value: byM.has(m) ? pick(byM.get(m)!) : null }));
        specs.push({
          title: d.name,
          brand: d.brandName,
          subtitle: `${d.brandName ?? 'sem marca'}${d.codigoInterno ? ` · ${d.codigoInterno}` : ''}`,
          tagline: `Saída de linha — ${DISC_STATUS_LABELS[d.status]}`,
          photoUrl: d.photoUrl,
          header: [
            { label: 'Marca', value: d.brandName ?? '—' },
            { label: 'Código', value: d.codigoInterno ?? '—' },
            { label: 'Início', value: new Date(d.startDate + 'T00:00:00').toLocaleDateString('pt-BR') },
            { label: 'Plano', value: `${d.planMonths} meses` },
          ],
          indicators: [
            { title: 'Faturamento', meta: d.metaFaturamento, points: pts((e) => e.faturamento), fmt: brl },
            { title: 'Margem de contribuição (MC [R])', meta: d.metaMargem, points: pts((e) => e.margemContribuicao), fmt: pct },
            { title: 'Clientes positivados', meta: d.metaClientes, points: pts((e) => e.clientesPositivados), fmt: (v) => (v == null ? '—' : String(v)) },
            { title: '% Devolução (NFD)', meta: d.metaNfd, points: pts((e) => e.nfd), fmt: pct, lowerIsBetter: true },
          ],
        });
      }
      const html = buildListReportHtml({
        title: 'Saída de linha — relatório geral',
        subtitle: `${filtered.length} item(ns) · gerado em ${new Date().toLocaleDateString('pt-BR')}`,
        items: await prepararFotos(specs),
        footerNote: 'Workflow de Novos Produtos — Trevo Lácteos',
      }, true);
      printReport(html, 'saida-linha-geral');
    } finally {
      setGerando(false);
    }
  }

  return (
    <>
      <div className="topbar">
        <div>
          <div className="muted" style={{ fontSize: 12 }}>Mix de produtos</div>
          <h1>Saída de linha</h1>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="btn-ghost" disabled={gerando || !filtered.length} onClick={relatorioGeral}>
            {gerando ? 'Gerando…' : '📄 Relatório geral (PDF)'}
          </button>
          {canManage && (
            <button className="btn-primary" onClick={() => setAdding(true)}>
              ＋ Novo item em acompanhamento
            </button>
          )}
        </div>
      </div>

      <div className="content">
        <p className="muted" style={{ marginTop: -4 }}>
          SKUs que estamos observando para decidir se saem do mix. O plano padrão é de 6 meses; a cada
          mês lançamos faturamento, margem de contribuição e clientes positivados e acompanhamos a
          evolução em relação à meta.
        </p>

        {pend.size > 0 && (
          <div className="card card-pad" style={{ borderLeft: '4px solid var(--warn, #d08700)' }}>
            ⏰ <b>{pend.size}</b> item(ns) ainda sem o lançamento de <b>{monthLabel(ultimoMesLiberado())}</b> (mês já
            fechado). Abra e preencha faturamento, margem e clientes positivados. O mês em andamento só é
            liberado após o fechamento, por volta do dia 10 do mês seguinte.
          </div>
        )}

        <div className="disc-toolbar">
          <input
            className="disc-search"
            placeholder="Buscar por nome, marca ou código…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)}>
            <option value="todas">Todas as marcas</option>
            {brandOptions.map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}>
            <option value="ativos">Em acompanhamento</option>
            <option value="todos">Todos</option>
            <option value="desativado">Desativados</option>
            <option value="voltou_mix">Voltaram ao mix</option>
          </select>
        </div>

        {loading ? (
          <p className="muted">Carregando…</p>
        ) : filtered.length === 0 ? (
          <p className="muted">Nenhum item encontrado.</p>
        ) : (
          <div className="proj-grid">
            {filtered.map((d) => {
              const months = planMonthsList(d.startDate, d.planMonths);
              const inicio = new Date(d.startDate + 'T00:00:00').toLocaleDateString('pt-BR');
              return (
                <button
                  className={`proj-card ${d.status !== 'em_acompanhamento' ? 'proj-inativo' : ''}`}
                  key={d.id}
                  onClick={() => nav(`/saida-linha/${d.id}`)}
                >
                  <div className="proj-card-top">
                    <span className={`seal ${d.status === 'em_acompanhamento' ? 'pend' : 'ok'}`}>
                      {DISC_STATUS_LABELS[d.status]}
                    </span>
                    {pend.has(d.id) && <span className="disc-pend-chip">⏰ falta lançar</span>}
                  </div>
                  <div className="proj-idrow">
                    {d.photoUrl ? (
                      <img className="proj-thumb" src={d.photoUrl} alt="" loading="lazy" />
                    ) : (
                      <div className="proj-thumb ph" aria-hidden="true">📦</div>
                    )}
                    <div className="proj-idtext">
                      <div className="proj-brand">
                        {d.brandName ?? 'sem marca'}
                        {d.codigoInterno ? ` – ${d.codigoInterno}` : ''}
                      </div>
                      <div className="proj-name">{d.name}</div>
                    </div>
                  </div>
                  <div className="proj-next">
                    <span className="proj-next-lbl">Plano</span>
                    <span className="proj-next-txt">
                      início {inicio} · {months.length} meses
                    </span>
                  </div>
                  <div className="proj-foot">
                    <span className="proj-meta" style={{ flexWrap: 'wrap' }}>
                      <span>Meta fim: <b>{brl(d.metaFaturamento)}</b></span>
                      {d.metaMargem != null && <span>MC <b>{d.metaMargem}%</b></span>}
                      {d.metaNfd != null && <span>Dev. <b>{d.metaNfd}%</b></span>}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {adding && currentOrgId && (
        <NovoItem
          orgId={currentOrgId}
          userId={userId}
          fullName={fullName}
          onClose={() => setAdding(false)}
          onCreated={(id) => {
            setAdding(false);
            nav(`/saida-linha/${id}`);
          }}
        />
      )}
    </>
  );
}

function NovoItem({
  orgId,
  userId,
  fullName,
  onClose,
  onCreated,
}: {
  orgId: string;
  userId: string | null;
  fullName: string | null;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [brands, setBrands] = useState<BrandRow[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [brandId, setBrandId] = useState('');
  const [newBrand, setNewBrand] = useState('');
  const [name, setName] = useState('');
  const [codigo, setCodigo] = useState('');
  const [motivo, setMotivo] = useState('');
  const [respId, setRespId] = useState('');
  const [start, setStart] = useState(new Date().toISOString().slice(0, 10));
  const [planMonths, setPlanMonths] = useState(6);
  const [covTempo, setCovTempo] = useState('');
  const [covValor, setCovValor] = useState('');
  const [metaFat, setMetaFat] = useState('');
  const [metaMc, setMetaMc] = useState('');
  const [metaCli, setMetaCli] = useState('');
  const [metaNfd, setMetaNfd] = useState('');
  const [metaNfdTouched, setMetaNfdTouched] = useState(false);
  const [coment, setComent] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [linkedPhoto, setLinkedPhoto] = useState<string | null>(null);
  const [linkInfo, setLinkInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    loadBrands(orgId).then((bs) => {
      setBrands(bs);
      setBrandId(bs[0]?.id ?? '__new__');
    });
    loadPeople(orgId).then(setPeople);
  }, [orgId]);

  const creatingBrand = brandId === '__new__';
  const numOrNull = (v: string) => (v.trim() === '' ? null : Number(v));

  // Mesmo produto na Rampa (por código): carrega nome, marca e foto.
  async function onCodigoBlur() {
    const found = await lookupProductByCodigo(orgId, codigo);
    if (!found) { setLinkInfo(null); return; }
    if (!name.trim()) setName(found.name);
    if (found.brandId && brands.some((b) => b.id === found.brandId)) setBrandId(found.brandId);
    if (found.photoUrl) setLinkedPhoto(found.photoUrl);
    const origem = found.from === 'rampa' ? 'Rampa de lançamentos' : 'Saída de linha';
    setLinkInfo(`Produto encontrado em ${origem} — dados${found.photoUrl ? ' e foto' : ''} carregados.`);
  }

  // Meta de % de devolução (NFD) por marca: Ehrmann e Apreciare Fit = 6%, demais = 4%.
  const brandName = creatingBrand ? newBrand : brands.find((b) => b.id === brandId)?.name ?? '';
  const nfdMetaForBrand = (nm: string) => {
    const n = nm.toLowerCase();
    return n.includes('ehrmann') || n.includes('apreciare fit') ? 6 : 4;
  };
  // Sugere a meta conforme a marca, até a pessoa digitar a dela.
  useEffect(() => {
    if (!metaNfdTouched) setMetaNfd(String(nfdMetaForBrand(brandName)));
  }, [brandName, metaNfdTouched]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!name.trim()) return setErr('Dê um nome ao produto.');
    setBusy(true);
    try {
      let finalBrandId: string | null = brandId === '__new__' ? null : brandId;
      if (creatingBrand) {
        const nb = newBrand.trim();
        if (!nb) {
          setBusy(false);
          return setErr('Informe o nome da nova marca.');
        }
        const { error, brand } = await createBrand(orgId, nb);
        if (error || !brand) {
          setBusy(false);
          return setErr(error ?? 'Não foi possível criar a marca.');
        }
        finalBrandId = brand.id;
      }
      const resp = people.find((p) => p.id === respId);
      const { error, id } = await createDiscontinuation({
        orgId,
        brandId: finalBrandId,
        name: name.trim(),
        codigoInterno: codigo.trim() || null,
        motivo: motivo.trim() || null,
        responsavelId: respId || null,
        responsavelNome: resp?.name ?? null,
        startDate: start || null,
        planMonths: planMonths || 6,
        coberturaTempo: covTempo.trim() || null,
        coberturaValor: numOrNull(covValor),
        metaFaturamento: numOrNull(metaFat),
        metaMargem: numOrNull(metaMc),
        metaClientes: numOrNull(metaCli),
        metaNfd: numOrNull(metaNfd),
        comentarios: coment.trim() || null,
        createdById: userId,
        createdByName: fullName,
      });
      if (error || !id) {
        setBusy(false);
        return setErr(error ?? 'Não foi possível criar o item.');
      }
      if (file) {
        const up = await uploadDiscImage(orgId, id, file);
        if (up.url) {
          await updateDiscontinuation(id, { photo_url: up.url });
          await syncProductPhoto(orgId, codigo.trim() || null, up.url);
        }
      } else if (linkedPhoto) {
        // Reaproveita a foto do mesmo produto na Rampa.
        await updateDiscontinuation(id, { photo_url: linkedPhoto });
      }
      onCreated(id);
    } catch (e) {
      setBusy(false);
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal card" onClick={(e) => e.stopPropagation()}>
        <div className="section-title">
          <h2>Novo item em acompanhamento</h2>
          <button className="btn-ghost btn-sm" onClick={onClose}>Fechar</button>
        </div>
        <form onSubmit={submit} className="modal-body">
          <label className="field">
            <span>Nome do produto</span>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex.: Iogurte Grego 90g Morango" autoFocus />
          </label>
          <label className="field">
            <span>Marca</span>
            <select value={brandId} onChange={(e) => setBrandId(e.target.value)}>
              {brands.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
              <option value="__new__">＋ Nova marca…</option>
            </select>
          </label>
          {creatingBrand && (
            <label className="field">
              <span>Nome da nova marca</span>
              <input value={newBrand} onChange={(e) => setNewBrand(e.target.value)} placeholder="Ex.: Trevo" />
            </label>
          )}
          <div className="capa-grid">
            <label className="field">
              <span>Código interno</span>
              <input value={codigo} onChange={(e) => setCodigo(e.target.value)} onBlur={onCodigoBlur} />
            </label>
            <label className="field">
              <span>Início do acompanhamento</span>
              <input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
            </label>
            <label className="field">
              <span>Prazo do plano (meses)</span>
              <input type="number" min={1} value={planMonths} onChange={(e) => setPlanMonths(Number(e.target.value))} />
            </label>
          </div>
          {linkInfo && <p className="muted" style={{ fontSize: 12, margin: '-4px 2px 2px', color: 'var(--brand-green)' }}>🔗 {linkInfo}</p>}
          <div className="capa-grid">
            <label className="field">
              <span>Cobertura de embalagens (tempo)</span>
              <input value={covTempo} onChange={(e) => setCovTempo(e.target.value)} placeholder="Ex.: 2 meses" />
            </label>
            <label className="field">
              <span>Cobertura de embalagens (R$)</span>
              <input type="number" value={covValor} onChange={(e) => setCovValor(e.target.value)} />
            </label>
            <label className="field">
              <span>Responsável pelo acompanhamento</span>
              <select value={respId} onChange={(e) => setRespId(e.target.value)}>
                <option value="">—</option>
                {people.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </label>
          </div>
          <label className="field">
            <span>Motivo da possível retirada</span>
            <textarea rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ex.: baixo giro / margem apertada / poucos clientes positivados" />
          </label>

          <h3 className="capa-sub">Metas para o último mês do plano</h3>
          <div className="capa-grid">
            <label className="field">
              <span>Meta de faturamento (R$)</span>
              <input type="number" value={metaFat} onChange={(e) => setMetaFat(e.target.value)} />
            </label>
            <label className="field">
              <span>Meta de margem de contribuição (%)</span>
              <input type="number" value={metaMc} onChange={(e) => setMetaMc(e.target.value)} />
            </label>
            <label className="field">
              <span>Meta de clientes positivados</span>
              <input type="number" value={metaCli} onChange={(e) => setMetaCli(e.target.value)} />
            </label>
            <label className="field">
              <span>Meta de % de devolução (NFD)</span>
              <input
                type="number"
                step="0.1"
                value={metaNfd}
                onChange={(e) => {
                  setMetaNfdTouched(true);
                  setMetaNfd(e.target.value);
                }}
              />
            </label>
          </div>
          <p className="muted" style={{ fontSize: 12, marginTop: -4 }}>
            Sugestão de meta de devolução: {nfdMetaForBrand(brandName)}% para {brandName || 'esta marca'}
            {' '}(Ehrmann e Apreciare Fit = 6%, demais marcas = 4%). Ajuste se precisar.
          </p>

          <label className="field">
            <span>Comentários</span>
            <textarea rows={2} value={coment} onChange={(e) => setComent(e.target.value)} />
          </label>
          <label className="field">
            <span>Imagem do produto</span>
            <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </label>

          {err && <p className="err">{err}</p>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
            <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
            <button className="btn-primary" disabled={busy}>{busy ? 'Salvando…' : 'Cadastrar'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
